import { useState } from 'react';
import { Invoice } from '@/lib/data';
import { getInvoices } from '@/services/invoiceService';
import { useQuery } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { toast } from 'sonner';
import { useAuth } from './useAuth';

export function useInvoiceList() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const { 
    data: invoiceData = { data: [], count: 0 }, 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ['invoices', page, pageSize],
    queryFn: () => getInvoices({ page, pageSize }),
    // Add staleTime to prevent unnecessary refetches
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Disable automatic refetching on window focus
    refetchOnWindowFocus: false
  });

  const invoices = invoiceData.data;
  const totalCount = invoiceData.count;
  const totalPages = Math.ceil(totalCount / pageSize);

  const filteredInvoices = invoices.filter(invoice => {
    if (!searchTerm) return true;
    
    const term = searchTerm.toLowerCase();
    return (
      invoice.invoice_id.toLowerCase().includes(term) ||
      (invoice.subscription?.customer.customer_name.toLowerCase().includes(term) || false) ||
      (invoice.subscription?.customer.customer_number.toLowerCase().includes(term) || false)
    );
  });

  return {
    invoices: filteredInvoices,
    isLoading,
    error,
    searchTerm,
    setSearchTerm,
    pagination: {
      page,
      setPage,
      pageSize,
      setPageSize,
      totalPages,
      totalCount
    }
  };
}

export interface InvoiceBatchOptions {
  month: number;
  year: number;
  testMode: boolean;
}

export function useInvoiceBatchProcess() {
  const [isProcessing, setIsProcessing] = useState(false);
  const { user } = useAuth();

  const processBatch = async (options: InvoiceBatchOptions) => {
    setIsProcessing(true);
    
    try {
      console.log('Calling process-invoice-batch with options:', options);
      
      // Check if user is authenticated
      if (!user || !user.id) {
        console.error('User not authenticated');
        toast.error('You must be logged in to generate invoices.');
        return { success: false };
      }
      
      // Call the Supabase edge function for batch invoice creation
      const { data, error } = await supabase.functions.invoke('process-invoice-batch', {
        body: {
          month: options.month,
          year: options.year,
          runType: options.testMode ? 'test' : 'live', // Convert boolean testMode to string runType
          user_id: user.id // Include the user_id from the authenticated user
        }
      });

      if (error) {
        console.error('Error calling process-invoice-batch:', error);
        toast.error('Failed to generate invoices. Please try again.');
        return { success: false };
      }
      
      console.log('Edge function result:', data);
      return { success: true, data };
      
    } catch (err) {
      console.error('Error in batch invoice processing:', err);
      toast.error('An error occurred while generating invoices.');
      return { success: false };
    } finally {
      setIsProcessing(false);
    }
  };

  const resetBatch = () => {
    // Reset function for UI state if needed
  };

  return {
    isProcessing,
    processBatch,
    resetBatch
  };
}
